import { gql } from '@apollo/client';

// ============================================================================
// USER PROFILE MUTATIONS
// Mutations for adding and managing user profiles on a customer account.
// ============================================================================

/**
 * ADD_USER_PROFILE_MUTATION
 *
 * Adds a new user profile (sub-user) to the customer's account.
 * The user will be able to log in with the BankID linked to the given identifier.
 *
 * @param customerId   - The customer's ID (currentCustomer.id)
 * @param email        - Email address for the new user
 * @param firstName    - First name of the new user
 * @param identifier   - Swedish personal number (12 digits, no dash)
 * @param lastName     - Last name of the new user
 * @param phoneNumber  - Phone number of the new user (e.g. "+46701234567")
 *
 * @returns The created UserProfile with id, createdAt, email, firstName, identifier, lastName, phoneNumber
 */
export const ADD_USER_PROFILE_MUTATION = gql`
	mutation AddUserProfile(
		$customerId: Int!
		$email: String!
		$firstName: String!
		$identifier: String!
		$lastName: String!
		$phoneNumber: String!
	) {
		addUserProfile(
			input: {
				customerId: $customerId
				email: $email
				firstName: $firstName
				identifier: $identifier
				lastName: $lastName
				phoneNumber: $phoneNumber
			}
		) {
			userProfile {
				createdAt
				email
				firstName
				id
				identifier
				lastName
				phoneNumber
			}
		}
	}
`;

/**
 * UPDATE_USER_PROFILE_ROLES_MUTATION
 *
 * Assigns (or removes) roles on an existing user profile.
 * Pass all roles to grant in rolesToAdd and any to revoke in rolesToRemove.
 *
 * @param customerId     - The customer's ID (currentCustomer.id)
 * @param userProfileId  - The ID of the user profile (returned from addUserProfile)
 * @param rolesToAdd     - Array of role claimName strings to grant
 * @param rolesToRemove  - Array of role claimName strings to revoke
 *
 * @returns The updated UserProfile
 */
/**
 * DELETE_USER_PROFILE_MUTATION
 *
 * Removes a user profile (sub-user) from the customer's account.
 *
 * @param customerId     - The customer's ID (currentCustomer.id)
 * @param userProfileId  - The ID of the user profile to delete
 *
 * @returns The deleted UserProfile
 */
export const DELETE_USER_PROFILE_MUTATION = gql`
	mutation DeleteUserProfile(
		$customerId: Int!
		$userProfileId: Int!
	) {
		deleteUserProfile(
			input: {
				customerId: $customerId
				userProfileId: $userProfileId
			}
		) {
			userProfile {
				createdAt
				email
				firstName
				id
				identifier
				isActive
				lastName
				phoneNumber
				updatedAt
			}
		}
	}
`;

/**
 * UPDATE_USER_PROFILE_MUTATION
 *
 * Updates an existing user profile's personal information.
 *
 * @param customerId     - The customer's ID (currentCustomer.id)
 * @param userProfileId  - The ID of the user profile to update
 * @param email          - Updated email address
 * @param firstName      - Updated first name
 * @param identifier     - Personal number (12 digits, no dash)
 * @param lastName       - Updated last name
 * @param phoneNumber    - Updated phone number
 *
 * @returns The updated UserProfile
 */
export const UPDATE_USER_PROFILE_MUTATION = gql`
	mutation UpdateUserProfile(
		$customerId: Int!
		$userProfileId: Int!
		$email: String!
		$firstName: String!
		$lastName: String!
		$phoneNumber: String!
	) {
		updateUserProfile(
			input: {
				customerId: $customerId
				userProfileId: $userProfileId
				email: $email
				firstName: $firstName
				lastName: $lastName
				phoneNumber: $phoneNumber
			}
		) {
			userProfile {
				createdAt
				email
				firstName
				id
				identifier
				lastName
				phoneNumber
				updatedAt
			}
		}
	}
`;

export const UPDATE_USER_PROFILE_ROLES_MUTATION = gql`
	mutation UpdateUserProfileRoles(
		$customerId: Int!
		$userProfileId: Int!
		$rolesToAdd: [String!]!
		$rolesToRemove: [String!]!
	) {
		updateUserProfileRoles(
			input: {
				customerId: $customerId
				userProfileId: $userProfileId
				rolesToAdd: $rolesToAdd
				rolesToRemove: $rolesToRemove
			}
		) {
			userProfile {
				createdAt
				email
				firstName
				id
				identifier
				isActive
				lastName
				phoneNumber
				updatedAt
			}
		}
	}
`;
